import React, { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import Dashboard from './pages/Dashboard';
import ShoppingList from './pages/ShoppingList';
import MealPlannerCalendar from './components/MealPlannerCalendar';
import LoginPage from './pages/LoginPage';
import AdminPage from './components/AdminPage';
import FamilyListBar from './components/FamilyListBar';
import { CurrentListProvider } from './context/CurrentListContext';
import { getCurrentUser, logout } from './services/api';
import './styles/global.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => Boolean(localStorage.getItem('auth_token'))
  );
  const [activeView, setActiveView] = useState('list');
  const [previousView, setPreviousView] = useState('list');
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const handleLogout = () => setIsAuthenticated(false);
    const handleAdminOpen = () => handleViewChange('admin');
    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('admin:open', handleAdminOpen);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('admin:open', handleAdminOpen);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewChange = (newView: string) => {
    if (newView === activeView) return;
    if (newView === 'admin') setPreviousView(activeView);

    setIsTransitioning(true);
    setTimeout(() => {
      setActiveView(newView);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 200);
  };

  const handleBackFromAdmin = () => handleViewChange(previousView);

  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard onViewChange={handleViewChange} activeView={activeView} />;
      case 'list':
        return <ShoppingList onViewChange={handleViewChange} activeView={activeView} />;
      case 'planner':
        return <MealPlannerCalendar onViewChange={handleViewChange} activeView={activeView} />;
      case 'admin':
        return <AdminPage onBack={handleBackFromAdmin} />;
      default:
        return <ShoppingList onViewChange={handleViewChange} activeView={activeView} />;
    }
  };

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  const currentUser = getCurrentUser();
  const isAdminUser = currentUser?.role === 'admin';
  const isAdmin = activeView === 'admin';
  const isDashboard = activeView === 'dashboard';
  const isPlanner = activeView === 'planner';
  const isScrollable = isDashboard || isPlanner;

  return (
    <CurrentListProvider>
    <div className={`w-full flex flex-col bg-gray-50 pt-5 ${isAdmin ? 'min-h-screen' : 'min-h-screen max-md:h-screen max-md:overflow-hidden'}`}>
      <div className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isAdmin ? '' : 'max-md:flex-1 max-md:flex max-md:flex-col max-md:overflow-hidden max-md:min-h-0'}`}>

        {/* Desktop Header — hidden on admin */}
        {!isAdmin && (
          <header className="desktop-header pt-6 flex-shrink-0 relative">
            <h1 className="text-4xl font-bold text-center text-primary mb-6">
              Organisateur Familial
            </h1>
            <Navigation activeView={activeView} onViewChange={handleViewChange} />

            {/* Family + list navigation */}
            {activeView === 'list' && (
              <div className="flex justify-center mt-4">
                <FamilyListBar />
              </div>
            )}

            {/* Top-right actions */}
            <div className="absolute top-6 right-0 flex items-center gap-1">
              {isAdminUser && (
                <button
                  type="button"
                  onClick={() => handleViewChange('admin')}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  aria-label="Administration"
                  title="Administration"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </button>
              )}
              <button
                type="button"
                onClick={logout}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                aria-label="Déconnexion"
                title="Déconnexion"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          </header>
        )}

        {/* Page content */}
        <div
          className={`
            transition-opacity duration-200 ease-in-out
            ${isAdmin ? '' : `max-md:flex-1 max-md:flex max-md:flex-col max-md:min-h-0 ${isScrollable ? 'max-md:overflow-y-auto' : 'max-md:overflow-hidden'}`}
            ${isTransitioning ? 'opacity-0' : 'opacity-100'}
          `}
        >
          {renderActiveView()}
        </div>
      </div>
    </div>
    </CurrentListProvider>
  );
}

export default App;
